import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, RotateCcw, FileSearch, Loader2 } from "lucide-react";

export const SCRUTINY_CHECKS = [
  ["identity", "Claimant identity & authority"],
  ["documents", "Authenticity of furnished evidence"],
  ["reconciliation", "PO ↔ invoice ↔ delivery reconciliation"],
  ["registry", "GSTIN / PAN registry cross-check"],
  ["supplier", "Supplier-side confirmation"],
  ["computation", "Commission computation & slab audit"],
];
const SLOTS = [
  ["po_copy", "PO copy"], ["invoice", "Tax invoice(s)"], ["delivery_receipt", "Delivery receipt"],
  ["weighbridge_slip", "Weighbridge slip"], ["bank_proof", "Bank proof"], ["other", "Other"],
];
const COLOR = { cleared: "#2FB999", queried: "#E0A030" };

export function ScrutinyControl({ c, api, onChange }) {
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const sc = c.scrutiny || {};
  const mark = async (key, state) => {
    setBusy(key + state);
    try { await api.post(`/admin/indirect-claims/${c.id}/scrutiny/${key}`, { state, note: notes[key] || "" }); toast.success(`Checkpoint ${state}`); setNotes((n) => ({ ...n, [key]: "" })); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-sm border border-[#C9A961]/30 bg-black/40 p-4" data-testid={`scrutiny-control-${c.id}`}>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] inline-flex items-center gap-2"><FileSearch size={12} /> Scrutiny checkpoints · {Object.values(sc).filter((r) => r?.state === "cleared").length}/6 cleared</div>
      <ul className="mt-3 divide-y divide-white/5">
        {SCRUTINY_CHECKS.map(([key, label]) => { const row = sc[key]; return (
          <li key={key} className="py-2" data-testid={`sc-row-${c.id}-${key}`} data-state={row?.state || "open"}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-body text-[12px] text-[#C9C9C2]">{label}
                {row?.state && <span className="ml-2 font-grotesk text-[9px] uppercase tracking-[0.2em]" style={{ color: COLOR[row.state] }}>{row.state}</span>}
              </span>
              <div className="flex items-center gap-1">
                <button data-testid={`sc-clear-${c.id}-${key}`} disabled={!!busy} onClick={() => mark(key, "cleared")} title="Mark cleared" className="p-1.5 rounded-sm border border-[#2FB999]/40 text-[#2FB999] hover:bg-[#2FB999]/10 disabled:opacity-40">{busy === key + "cleared" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}</button>
                <button data-testid={`sc-query-${c.id}-${key}`} disabled={!!busy} onClick={() => mark(key, "queried")} title="Raise query" className="p-1.5 rounded-sm border border-[#E0A030]/40 text-[#E0A030] hover:bg-[#E0A030]/10 disabled:opacity-40">{busy === key + "queried" ? <Loader2 size={12} className="animate-spin" /> : <HelpCircle size={12} />}</button>
                <button data-testid={`sc-reset-${c.id}-${key}`} disabled={!!busy || !row} onClick={() => mark(key, "reset")} title="Reset" className="p-1.5 rounded-sm border border-white/10 text-[#8A8A82] hover:bg-white/5 disabled:opacity-30"><RotateCcw size={12} /></button>
              </div>
            </div>
            <input value={notes[key] || ""} onChange={(e) => setNotes((n) => ({ ...n, [key]: e.target.value }))} data-testid={`sc-note-${c.id}-${key}`} placeholder={row?.note ? `On record: ${row.note}` : "Note to claimant (optional)"} className="input mt-1.5 !py-1 !text-[11px]" />
          </li>); })}
      </ul>
    </div>
  );
}

export function EvidenceCallPanel({ c, api, onChange }) {
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ec = c.claim_form?.evidence_call;
  const issue = async () => {
    if (!slots.length) { toast.error("Select at least one document slot."); return; }
    if (reason.trim().length < 5) { toast.error("State the grounds for the notice."); return; }
    setBusy(true);
    try { const r = await api.post(`/admin/indirect-claims/${c.id}/evidence-call`, { slots, reason: reason.trim() }); toast.success(r.data.emailed ? "Evidence Call Notice issued and emailed" : "Notice issued (email failed)"); setSlots([]); setReason(""); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-sm border border-[#C0392B]/30 bg-black/40 p-4 mt-4" data-testid={`evidence-call-panel-${c.id}`}>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C0392B]">Evidence Call Notice</div>
      {ec && (
        <div className="mt-2 font-body text-[12px] text-[#C9C9C2]" data-testid={`ec-status-${c.id}`} data-state={ec.status}>
          <span className="font-grotesk text-[9px] uppercase tracking-[0.2em]" style={{ color: ec.status === "open" ? "#E0A030" : "#2FB999" }}>{ec.status === "open" ? "OPEN · awaiting claimant" : `FURNISHED · ${ec.documents_added} doc(s)`}</span>
          <div className="text-[#8A8A82] mt-1">{ec.slots.join(", ")} · {ec.reason}</div>
          {ec.furnished_note && <div className="italic text-[#8A8A82]">Claimant: {ec.furnished_note}</div>}
        </div>
      )}
      {!(ec?.status === "open") && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {SLOTS.map(([k, l]) => (
              <label key={k} className={`cursor-pointer rounded-sm border px-2 py-1 font-body text-[11px] ${slots.includes(k) ? "border-[#C0392B] text-[#F5F5F0] bg-[#C0392B]/15" : "border-white/10 text-[#8A8A82]"}`}>
                <input type="checkbox" className="hidden" data-testid={`ec-slot-${c.id}-${k}`} checked={slots.includes(k)} onChange={() => setSlots((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k])} />{l}
              </label>
            ))}
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`ec-reason-${c.id}`} placeholder="Grounds for the notice (sent to claimant)" className="input mt-2" />
          <button data-testid={`ec-issue-${c.id}`} disabled={busy} onClick={issue} className="mt-2 rounded-sm bg-[#C0392B] hover:bg-red-700 text-white px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] disabled:opacity-40">Issue notice</button>
        </>
      )}
    </div>
  );
}
